import { HudDisplayVisibility } from "@shared/types/organization";

// Organization - HUD Display Visibility
export const hudDisplayVisibilityOptions = [
  {
    value: HudDisplayVisibility.ALL_MEMBERS,
    label: "All team members",
    description: "HUD is visible to all team members",
  },
  {
    value: HudDisplayVisibility.ADMINS_ONLY,
    label: "Admins only",
    description: "HUD is visible only to administrators",
  },
  {
    value: HudDisplayVisibility.HIDDEN,
    label: "Hidden",
    description: "HUD is completely hidden",
  },
];

// Helper function to find option by value
export const findSelectOptionByValue = (options: any[], value: any) => {
  return options.find((option) => option.value === value);
};

// Helper function to get option label
export const getOptionLabel = (options: any[], value: any) => {
  const option = findSelectOptionByValue(options, value);
  return option ? option.label : "---";
};

export const hudDisplayVisibilityNames = (name: string) => {
  const option = findSelectOptionByValue(hudDisplayVisibilityOptions, name);
  return option ? option.label : "---";
};

// Job - Auto-generate AI Summary Setting (Job#auto_generate_ai_summaries)
export type AutoGenerateAiSummaries = "default" | "enabled" | "disabled";

export const jobAutoGenerateAiSummariesOptions: { value: AutoGenerateAiSummaries; label: string }[] = [
  { value: "default", label: "Use organization default" },
  { value: "enabled", label: "Enabled — auto-generate for every new applicant" },
  { value: "disabled", label: "Disabled — only generate on demand" },
];

// WhatJobs - Work From (jobWorkFrom)
export const whatJobsWorkFromOptions = [
  { value: "home", label: "Home" },
  { value: "hybrid", label: "Hybrid" },
  { value: "remote", label: "Remote" },
  { value: "onsite", label: "Onsite" },
];

const remotenessToWorkFromMap: Record<string, string> = {
  remoteness_only: "Remote",
  remoteness_hybrid: "Hybrid",
  remoteness_optional: "Hybrid",
  remoteness_none: "Onsite",
};

export const deriveWorkFrom = (remoteness: string): string => {
  return remotenessToWorkFromMap[remoteness];
};

// WhatJobs - Employment Type (jobEmploymentType)
export const whatJobsEmploymentTypeOptions = [
  { value: "full_time", label: "Full time" },
  { value: "part_time", label: "Part Time" },
];

// WhatJobs - Employment Sub Type (jobEmploymentSubType)
export const whatJobsEmploymentSubTypeOptions = [
  { value: "contract", label: "Contract" },
  { value: "seasonal", label: "Seasonal" },
  { value: "temporary", label: "Temporary" },
  { value: "permanent", label: "Permanent" },
  { value: "freelance", label: "Freelance" },
  { value: "apprenticeship", label: "Apprenticeship" },
  { value: "graduate", label: "Graduate" },
];

// WhatJobs - Schedule Type (jobScheduleType)
export const whatJobsScheduleTypeOptions = [
  { value: "eight_hour_shift", label: "8 hour shift" },
  { value: "ten_hour_shift", label: "10 hour shift" },
  { value: "twelve_hour_shift", label: "12 hour shift" },
  { value: "day_shift", label: "Day shift" },
  { value: "night_shift", label: "Night shift" },
  { value: "monday_to_friday", label: "Monday to Friday" },
  { value: "holiday", label: "Holiday" },
  { value: "weekend_availability", label: "Weekend availability" },
  { value: "weekends_only", label: "Weekends only" },
  { value: "no_weekends", label: "No weekends" },
  { value: "overtime", label: "Overtime" },
  { value: "other", label: "Other" },
];

// WhatJobs - Start Type (jobStartType)
export const whatJobsStartTypeOptions = [
  { value: "upon_acceptance", label: "To be agreed upon acceptance" },
  { value: "asap", label: "As soon as possible" },
  { value: "specific_date", label: "Specific Date" },
];

// WhatJobs - Salary Display Type (salaryDisplayType)
export const whatJobsSalaryDisplayTypeOptions = [
  { value: "negotiable", label: "Negotiable" },
  { value: "not_disclosed", label: "Not disclosed" },
  { value: "disclose", label: "Disclose" },
];

// WhatJobs - Contract Period (jobPeriod)
export const whatJobsContractPeriodOptions = [
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

// Helper functions to derive default values from job.kind
export const deriveEmploymentType = (jobKind: string): string => {
  return jobKind === "part_time" ? "part_time" : "full_time";
};

const jobKindToEmploymentSubTypeMap: Record<string, string> = {
  contract: "contract",
  temporary: "temporary",
  internship: "apprenticeship",
  full_time: "permanent",
  part_time: "permanent",
  part_time_or_full_time: "permanent",
  other: "",
  volunteer: "",
};

export const deriveEmploymentSubType = (jobKind: string): string => {
  return jobKindToEmploymentSubTypeMap[jobKind] || "";
};

// Job Application - Candidate list sort
export interface JobApplicationSortOption {
  key: string;
  label: string;
  sort: string;
  sortDirection: string;
}

export const jobApplicationSortOptions: JobApplicationSortOption[] = [
  {
    key: "last_activity_desc",
    label: "Newest activity",
    sort: "last_activity",
    sortDirection: "desc",
  },
  {
    key: "last_activity_asc",
    label: "Oldest activity",
    sort: "last_activity",
    sortDirection: "asc",
  },
  { key: "created_at_desc", label: "Newest applied", sort: "created_at", sortDirection: "desc" },
  { key: "created_at_asc", label: "Oldest applied", sort: "created_at", sortDirection: "asc" },
  { key: "fit_desc", label: "Best Plato fit", sort: "fit", sortDirection: "desc" },
  { key: "fit_asc", label: "Weakest Plato fit", sort: "fit", sortDirection: "asc" },
  { key: "last_name_asc", label: "Last name A–Z", sort: "last_name", sortDirection: "asc" },
  { key: "last_name_desc", label: "Last name Z–A", sort: "last_name", sortDirection: "desc" },
  { key: "first_name_asc", label: "First name A–Z", sort: "first_name", sortDirection: "asc" },
  { key: "first_name_desc", label: "First name Z–A", sort: "first_name", sortDirection: "desc" },
];
